/* Ported from the vhc prototype's kit (src/pages/AIGenerated/vhc/components). We had the
   `link-inline` mixin and no component; this is the component form, and it keeps the
   underline at rest — the non-colour cue has to be there before the pointer is. */
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import s from './TextLink.module.scss';

type Common = { children: ReactNode; className?: string };

/**
 * A link in running text or under a field — "Add details", "Show in Files",
 * "Clear filters", the missing-item jumps on the profile.
 *
 * Teal and underlined, which is the app's answer twice over: teal is what
 * "live" looks like everywhere else, and the underline is the non-colour cue
 * (1.4.1) — a link that is only a colour is not a link to anyone who cannot see
 * the colour. Kept at rest rather than revealed on hover, because a touch user
 * has no hover to reveal it with.
 *
 * Distinct from DrillLink, which is the ghost button in a section header that
 * leads out to the whole of something. This one lives inside content.
 *
 * Give it `to` and it navigates; give it `onClick` and it is a button that
 * happens to read as a link — the two must not be swapped, since a thing that
 * goes somewhere needs an address and a thing that acts must not have one.
 *
 * @alias inline link, anchor, hyperlink, text button
 */
export const TextLink = ({
  to,
  onClick,
  children,
  className,
  ...rest
}: (Common & { to: string; onClick?: never }) | (Common & { onClick: () => void; to?: never })) => {
  if (to !== undefined) {
    return (
      <Link to={to} className={clsx(s.link, className)} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <button type="button" className={clsx(s.link, className)} onClick={onClick} {...rest}>
      {children}
    </button>
  );
};
